import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionDebitsMagasinier() {
    const [produits, setProduits] = useState([]);
    const [produitsFiltres, setProduitsFiltres] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedProduits, setSelectedProduits] = useState([]);
    const [quantiteTemp, setQuantiteTemp] = useState(1);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [motif, setMotif] = useState('');
    
    const searchRef = useRef(null);
    const dropdownRef = useRef(null);

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    useEffect(() => {
        chargerProduits();
    }, []);

    useEffect(() => {
        if (search.trim() === '') {
            setProduitsFiltres([]);
            setShowDropdown(false);
            return;
        }
        const filtered = produits.filter(p => 
            p.nom.toLowerCase().includes(search.toLowerCase())
        );
        setProduitsFiltres(filtered);
        setShowDropdown(filtered.length > 0);
        setSelectedIndex(-1);
    }, [search, produits]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target) &&
                dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const chargerProduits = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/magasinier/get_produits.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProduits(data.produits);
            }
        } catch (error) {
            toast.error('Erreur chargement');
        }
    };

    const ajouterProduit = (produit) => {
        if (quantiteTemp <= 0) {
            toast.error('Quantité invalide');
            return;
        }
        if (quantiteTemp > produit.quantite) {
            toast.error(`Stock insuffisant (${produit.quantite} restants)`);
            return;
        }
        
        const existant = selectedProduits.find(p => p.id_produit === produit.id_produit);
        if (existant) {
            setSelectedProduits(selectedProduits.map(p =>
                p.id_produit === produit.id_produit ? { ...p, quantite: p.quantite + quantiteTemp } : p
            ));
        } else {
            setSelectedProduits([...selectedProduits, { ...produit, quantite: quantiteTemp }]);
        }
        
        setSearch('');
        setShowDropdown(false);
        setQuantiteTemp(1);
        toast.success(`${produit.nom} ajouté (${quantiteTemp})`);
    };

    const supprimerProduit = (id) => {
        setSelectedProduits(selectedProduits.filter(p => p.id_produit !== id));
    };

    const modifierQuantite = (id, nouvelleQuantite) => {
        const produit = produits.find(p => p.id_produit === id);
        if (nouvelleQuantite > produit.quantite) {
            toast.error(`Stock max: ${produit.quantite}`);
            return;
        }
        if (nouvelleQuantite <= 0) {
            supprimerProduit(id);
            return;
        }
        setSelectedProduits(selectedProduits.map(p =>
            p.id_produit === id ? { ...p, quantite: nouvelleQuantite } : p
        ));
    };

    const envoyerProgramme = async () => {
        if (selectedProduits.length === 0) {
            toast.error('Aucun produit sélectionné');
            return;
        }
        
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // D'abord consommer les produits
            for (const produit of selectedProduits) {
                await fetch(`${API_BASE}/magasinier/consommer_produit.php`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id_produit: produit.id_produit,
                        quantite: produit.quantite,
                        motif: motif || 'Programme journalier'
                    })
                });
            }
            
            // Envoyer le programme à l'intendant
            const response = await fetch(`${API_BASE}/magasinier/envoyer_programme.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ produits: selectedProduits })
            });
            const data = await response.json();
            
            if (data.success) {
                toast.success('Programme envoyé à l\'intendant');
                setSelectedProduits([]);
                setMotif('');
                chargerProduits();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (!showDropdown) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, produitsFiltres.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            ajouterProduit(produitsFiltres[selectedIndex]);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-5xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">📦 Programme du jour - Magasin</h1>
                    <p className="mt-2 opacity-90">Sélectionnez les produits pour le programme du jour</p>
                </div>

                {/* Barre de recherche Google-style */}
                <div className="mb-6" ref={searchRef}>
                    <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl">🔍</span>
                        <input
                            type="text"
                            placeholder="Rechercher un produit..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full rounded-2xl border border-slate-200 py-4 pl-12 pr-12 text-base outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 shadow-sm"
                            autoComplete="off"
                        />
                        {search && (
                            <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                ✕
                            </button>
                        )}
                    </div>
                    
                    {showDropdown && (
                        <div ref={dropdownRef} className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden max-h-80 overflow-y-auto" style={{ maxWidth: 'calc(100% - 3rem)', marginLeft: '1.5rem', marginRight: '1.5rem' }}>
                            {produitsFiltres.map((p, idx) => (
                                <div key={p.id_produit} onClick={() => ajouterProduit(p)} className={`p-4 cursor-pointer transition hover:bg-sky-50 border-b border-slate-100 last:border-0 ${idx === selectedIndex ? 'bg-sky-50' : ''}`}>
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <span className="font-semibold text-slate-800">{p.nom}</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-slate-500">{p.categorie}</span>
                                                <span className="text-xs text-emerald-600">Stock: {p.quantite} {p.unite}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="number"
                                                min="1"
                                                max={p.quantite}
                                                value={quantiteTemp}
                                                onChange={(e) => setQuantiteTemp(parseInt(e.target.value) || 1)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-center"
                                            />
                                            <button className="bg-sky-600 text-white px-4 py-1 rounded-lg text-sm">Ajouter</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Liste des produits sélectionnés */}
                <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">📋 Produits sélectionnés</h2>
                    
                    {selectedProduits.length === 0 ? (
                        <p className="text-center text-slate-500 py-8">Aucun produit sélectionné</p>
                    ) : (
                        <div className="space-y-3">
                            {selectedProduits.map(p => (
                                <div key={p.id_produit} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                    <div className="flex-1">
                                        <p className="font-medium">{p.nom}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="number"
                                            min="1"
                                            max={p.quantite}
                                            value={p.quantite}
                                            onChange={(e) => modifierQuantite(p.id_produit, parseInt(e.target.value) || 1)}
                                            className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-center"
                                        />
                                        <span className="text-sm text-slate-500">{p.unite}</span>
                                        <button onClick={() => supprimerProduit(p.id_produit)} className="text-red-500 hover:text-red-700">
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Motif / Destination</label>
                        <textarea
                            value={motif}
                            onChange={(e) => setMotif(e.target.value)}
                            placeholder="Ex: Service restauration, Livraison cantine..."
                            className="w-full rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-sky-400"
                            rows="2"
                        />
                    </div>
                    
                    <button
                        onClick={envoyerProgramme}
                        disabled={loading || selectedProduits.length === 0}
                        className="mt-4 w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Envoi en cours...
                            </>
                        ) : (
                            '📤 Envoyer le programme à l\'intendant'
                        )}
                    </button>
                </div>

                {/* Résumé */}
                {selectedProduits.length > 0 && (
                    <div className="rounded-2xl bg-gradient-to-r from-sky-50 to-blue-50 p-4 text-center">
                        <p className="text-sm text-slate-600">
                            Total: <strong>{selectedProduits.reduce((sum, p) => sum + p.quantite, 0)}</strong> unités pour <strong>{selectedProduits.length}</strong> produits
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}